import { Component, OnDestroy, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from 'src/app/@core/services/api.service';
import { ConnectionService } from 'src/app/@core/services/connections.service';
import { LoadingService } from 'src/app/@core/services/loading.service';
import { RpcService } from 'src/app/@core/services/rpc.service';
import { SocketService } from 'src/app/@core/services/socket.service';
import { CollatorManifestService, ManifestFetchResult } from 'src/app/@core/services/collator-manifest.service';
import { environment } from 'src/environments/environment';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'servers-dialog',
  templateUrl: './servers.component.html',
  styleUrls: ['./servers.component.scss']
})
export class ServersDialog implements OnInit, OnDestroy {
  public orderbookServers: string[] = [environment.ENDPOINTS?.[this.network]?.orderbookApiUrl, '@custom'];
  public apiServers: string[] = [environment.ENDPOINTS?.[this.network]?.relayerUrl, '@custom'];

  public customApiUrl: string = '';
  public customOrderbookUrl: string = '';

  selectedOrderbookServer: string = this.orderbookServers[0];
  selectedApiServer: string = this.apiServers[0];

  collatorWsUrl: string = 'ws://127.0.0.1:8787/ws';
  collatorManifest: ManifestFetchResult | null = null;

  // FIX: Declare the subscription array properly!
  private socketSubscriptions: Subscription[] = [];

  constructor(
    private socketService: SocketService,
    private rpcService: RpcService,
    private connectionService: ConnectionService,
    private toastrService: ToastrService,
    private apiService: ApiService,
    private loadingService: LoadingService,
    private collatorManifests: CollatorManifestService,
  ) {}

  get isOrderbookConnected() {
    return this.connectionService.isOBSocketConnected;
  }

  get isApiConnected() {
    return !!this.apiService.apiUrl;
  }

  get network() {
    return this.rpcService.NETWORK as string;
  }

  ngOnInit() {
    this.socketSubscriptions.push(
      this.socketService.events$
        .pipe(filter(({ event }) => event === 'connect'))
        .subscribe(() => {
          const orderbookUrl = this.selectedOrderbookServer === "@custom"
            ? this.customOrderbookUrl
            : this.selectedOrderbookServer;
          this.apiService.orderbookUrl = orderbookUrl;
        })
    );

    this.socketSubscriptions.push(
      this.socketService.events$
        .pipe(filter(({ event }) => event === 'disconnect'))
        .subscribe(() => {
          this.apiService.orderbookUrl = null;
        })
    );
  }

  ngOnDestroy() {
    this.socketSubscriptions.forEach((sub: Subscription) => sub.unsubscribe());
    this.socketSubscriptions = [];
  }

  selectOrderbookServer(url: string) {
    if (this.selectedOrderbookServer === url) return;
    if (this.isOrderbookConnected) {
      this.toastrService.warning('First disconnect to the connected server');
      return;
    }
    this.selectedOrderbookServer = url;
    this.customOrderbookUrl = '';
  }

  connectOrderbookServer() {
    const orderbookUrl = this.selectedOrderbookServer === "@custom"
      ? this.customOrderbookUrl
      : this.selectedOrderbookServer;
    this.socketService.obSocketConnect(orderbookUrl);
  }
  
  disconnectOrderbookServer() {
    this.socketService.obSocketDisconnect();
  }

  selectApiServer(url: string) {
    if (this.selectedApiServer === url) return;
    if (this.isApiConnected) {
      this.toastrService.warning('First disconnect to the connected server');
      return;
    }
    this.selectedApiServer = url;
    this.customApiUrl = '';
  }

  async connectApiServer() {
    try {
      this.loadingService.isLoading = true;
      const apiUrl = this.selectedApiServer === "@custom"
        ? this.customApiUrl
        : this.selectedApiServer;
      this.apiService.apiUrl = apiUrl;
      await this.rpcService.checkNetworkInfo();
      this.loadingService.isLoading = false;
    } catch (error: any) {
      this.toastrService.error(error.message, 'Connection Error');
      this.loadingService.isLoading = false;
    }
  }

  disconnectApiServer() {
    this.apiService.apiUrl = null;
  }

  async fetchCollatorManifest() {
    try {
      this.loadingService.isLoading = true;
      this.collatorManifest = await this.collatorManifests.fetch(this.collatorWsUrl);
      if (!this.collatorManifest.verified) {
        this.toastrService.warning(this.collatorManifest.reason || 'manifest unverified', 'Collator Manifest');
      }
    } catch (e: any) {
      this.toastrService.error(e?.message || String(e), 'Collator Manifest');
    } finally {
      this.loadingService.isLoading = false;
    }
  }
}
